import { FC } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { RouteType } from "shared/routes/types";
import { useDynamicRouting } from "./useDynamicRouting";

export type Breadcrumb = {
  title: string;
  pathname?: string;
  icon?: FC;
  isFreeToPlay?: boolean;
};

export type Breadcrumbs = Breadcrumb[];

export const useBreadcrumbs = (): Breadcrumbs => {
  const { pathname: currentPath } = useLocation();
  const { dynamicRoutes } = useDynamicRouting();

  const isFreeToPlayPath = /^\/brands\/free\/pages\/[^/]+$/.test(currentPath);

  if (isFreeToPlayPath) {
    const pagesRoute = dynamicRoutes.find((route) =>
      route.elements?.some((element) =>
        element.pathname?.startsWith("/brands/free/pages"),
      ),
    );

    return [
      {
        title: "Free to Play",
        pathname: currentPath,
        icon: pagesRoute?.Icon,
        isFreeToPlay: true,
      },
      {
        title: "Free to Play",
      },
    ];
  }

  // Original breadcrumbs logic for other paths
  const breadcrumbs = dynamicRoutes.reduce(
    (acc: Breadcrumbs, route: RouteType) => {
      const hasSubRoutesOfRootPath = Boolean(route.elements?.length);

      // Handle sub routes of dynamicRoutes root path
      // Breadcrumb mostly are used to handle deep nested routes
      if (hasSubRoutesOfRootPath) {
        route.elements?.forEach((subElement) => {
          if (
            subElement.pathname &&
            currentPath.includes(subElement.pathname)
          ) {
            // Add the root route to the breadcrumbs. Pathname must be equal to subElement.pathname
            // to prevent redirecting on invalid page
            acc.push({
              title: route.title,
              pathname: subElement.pathname,
              icon: route.Icon,
            });

            if (
              subElement.nestedRoute?.pathname &&
              matchPath(subElement.nestedRoute?.pathname, currentPath)
            ) {
              // Add the sub route to the breadcrumbs as redirected link in terms of having nestedRoute
              acc.push({
                title: subElement.title,
                pathname: subElement.pathname,
              });
            } else {
              // Add the sub route to the breadcrumbs as typography
              acc.push({
                title: subElement.title || "",
              });
            }
          }
        });
      }

      return acc;
    },
    [],
  );

  return breadcrumbs;
};
